import React, { Suspense, lazy, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import SmoothScroll from "./components/SmoothScroll";
import PageTransition from "./components/PageTransition";
import { TransitionProvider } from "./context/TransitionContext";
import { ProjectProvider } from "./context/ProjectContext";
import ContactUs from "./pages/ContactUs";
import About from "./pages/About/About";
import PremiumLoader from "./components/PremiumLoader";
import ChatBot from "./components/ChatBot/ChatBot";
import "./App.css";

import { ContactProvider, useContactModal } from "./context/ContactContext";

// Lazy-loaded pages
const Home = lazy(() => import("./pages/Home"));
const Services = lazy(() => import("./pages/Services"));
const Work = lazy(() => import("./pages/Work"));
const ProjectDetail = lazy(() => import("./pages/ProjectDetail"));
const SampleWork = lazy(() => import("./pages/SampleWork"));
const SampleWorkPreview = lazy(() => import("./pages/SampleWorkPreview"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminLeads = lazy(() => import("./pages/AdminLeads"));

// Contact route trigger wrapper
const ContactRouteHandler = () => {
  const { openContactModal } = useContactModal();
  React.useEffect(() => {
    openContactModal();
  }, [openContactModal]);

  return <Home />;
};

// Wrapper to trigger PageTransition and ensure all pages open from top (0,0)
const RouteWrapper = ({ children }) => {
  const location = useLocation();

  React.useLayoutEffect(() => {
    if (typeof window !== "undefined" && "scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;

    const timer = setTimeout(() => {
      window.scrollTo(0, 0);
    }, 50);

    return () => clearTimeout(timer);
  }, [location.pathname]);

  return (
    <>
      <PageTransition key={location.pathname} />
      {children}
    </>
  );
};

const App = () => {
  const [showInitialLoader, setShowInitialLoader] = useState(true);

  if (showInitialLoader) {
    return <PremiumLoader onComplete={() => setShowInitialLoader(false)} />;
  }

  return (
    <Router>
      <ProjectProvider>
        <ContactProvider>
          <TransitionProvider>
            <SmoothScroll>
              <Suspense fallback={null}>
                <ContactUs />
                <ChatBot />
                <RouteWrapper>
                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/services" element={<Services />} />
                    <Route path="/contact-us" element={<ContactRouteHandler />} />
                    <Route path="/projects" element={<Work />} />
                    <Route path="/sample-work" element={<SampleWork />} />
                    <Route
                      path="/sample-work/:id"
                      element={<SampleWorkPreview key={window.location.pathname} />}
                    />
                    <Route path="/about" element={<About />} />
                    <Route
                      path="/work/:slug"
                      element={<ProjectDetail key={window.location.pathname} />}
                    />
                    <Route path="/admin" element={<AdminDashboard />} />
                    <Route path="/admin/leads" element={<AdminLeads />} />
                  </Routes>
                </RouteWrapper>
              </Suspense>
            </SmoothScroll>
          </TransitionProvider>
        </ContactProvider>
      </ProjectProvider>
    </Router>
  );
};

export default App;
